<script setup>
import router from '../router';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

const { t } = useI18n();

const props = defineProps({
  transaction: {
    type: Object,
    default: () => ({})
  },
  darkmode: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['edit', 'pay']);

function formatMoney(v) {
  const total = parseFloat(v || 0);
  return total.toLocaleString(t('message.global.date_language'), {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatDate(d) {
  return new Date(d).toLocaleDateString(t('message.global.date_language'), { timeZone: 'UTC' });
}

const isOverdue = computed(() => {
  const date = new Date(props.transaction.date);
  return date.setDate(date.getDate() + 1) < new Date() && props.transaction.status == 'Unpaid';
});

const amountClass = computed(() => {
  if (props.transaction.type == 'despesa') return 'is-expense';
  if (props.transaction.type == 'receita') return 'is-income';
  return 'is-transfer';
});

const statusLabel = computed(() => {
  if (props.transaction.type == 'transferência') return t('message.global.transfer');
  if (props.transaction.status == 'Paid') {
    return props.transaction.type == 'receita'
      ? t('message.global.received')
      : t('message.global.payed');
  }
  if (isOverdue.value) return t('message.global.overdue');
  return t('message.global.predicted');
});

const statusClass = computed(() => {
  if (isOverdue.value) return 'status-overdue';
  if (props.transaction.status == 'Paid') return 'status-paid';
  return 'status-pending';
});

const cardVariantClass = computed(() => {
  if (props.transaction.type == 'receita') return 'card-income';
  if (props.transaction.type == 'despesa') return 'card-expense';
  return 'card-transfer';
});

const categoryStyle = computed(() => ({
  backgroundColor: props.transaction.category_color+'20' || '#e9ecef20',
  color: props.transaction.category_color || '#e9ecef'
}));

const payIconClass = computed(() => {
  if (props.transaction.status == 'Unpaid') {
    return props.darkmode ? 'fal text-white-50' : 'fal text-black-50';
  }
  return 'fas text-success';
});

const payTooltip = computed(() => {
  return props.transaction.type == 'despesa'
    ? props.transaction.status == 'Unpaid'
      ? t('message.transactions.paying_button_expense')
      : t('message.global.payed')
    : props.transaction.status == 'Unpaid'
      ? t('message.transactions.paying_button_revenue')
      : t('message.global.received');
});

const subtitle = computed(() => {
  if (props.transaction.type == 'transferência') {
    return `${props.transaction.account_name} para ${props.transaction.account_destiny_name}`;
  }

  if (props.transaction.customer_suplier_id > 0) {
    return `para ${props.transaction.customer_supplier_name}`;
  }

  return props.transaction.account_name || '';
});

const recurrenceLabel = computed(() => {
  if (props.transaction.recurrence == 'Installments') {
    return `(${props.transaction.current_installment}/${props.transaction.end_installment})`;
  }
  return '';
});

const editTrasaction = (data) => {
  if (data.invoice_id > 0) {
    router.push('/fatura/' + data.invoice_id);
  } else {
    emit('edit', data);
  }
};

const payTrasaction = (data) => {
  emit('pay', data);
};

function getContrastYIQ(hexcolor) {
  const safeHex = hexcolor || '#e9ecef';
  const r = parseInt(safeHex.substring(1, 3), 16);
  const g = parseInt(safeHex.substring(3, 5), 16);
  const b = parseInt(safeHex.substring(5, 7), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return yiq >= 128 ? 'black' : 'white';
}
</script>

<template>
  <div
    :id="'transaction' + transaction.id"
    class="transaction-modern-card"
    :class="[
      cardVariantClass,
      amountClass,
      {
        darkmode: darkmode,
        paid: transaction.status == 'Paid',
        overdue: isOverdue
      }
    ]"
  >
    <div
      class="transaction-click-area"
    >
      <div 
        class="transaction-left" 
        @click="editTrasaction(transaction)"
        :data-bs-toggle="transaction.invoice_id > 0 ? null : 'modal'"
      :data-bs-target="transaction.invoice_id > 0 ? null : '#detailsTransaction'"
      >
        <div
          class="transaction-category-icon d-none d-md-flex"
          :style="categoryStyle"
          v-tooltip="{ content: transaction.category_name, distance: 15 }"
        >
          <i :class="'fas ' + transaction.category_icon"></i>
        </div>

        <div class="transaction-main-content">
          <div class="transaction-top-line">

            <span class="transaction-status-badge d-md-none" :class="statusClass">
              {{ statusLabel }}
            </span>

            <div class="transaction-title-wrap">
              <div
                class="transaction-title"
                v-tooltip="{ content: t('message.global.see_details'), distance: 15 }"
              >
                {{ transaction.description }} {{ recurrenceLabel }}
                <span class="text-muted fs-10 mt-n2" v-if="(transaction.customer_suplier_id > 0)">
                  <br>
                  para {{ transaction.customer_supplier_name }}
                </span>
              </div>
            </div>

            <span class="transaction-status-badge d-none d-md-flex" :class="statusClass">
              {{ statusLabel }}
            </span>
          </div>

          <div class="transaction-meta-line d-md-none">
            <span class="category-chip" :style="categoryStyle">
              <i class="me-1" :class="'fas ' + transaction.category_icon"></i>
              {{ transaction.category_name }}
            </span>

            <span class="account-chip">
              <img :src="transaction.account_icon" width="14" alt="" class="rounded-circle me-1">
              {{ transaction.account_name }}
            </span>
          </div>
        </div>
      </div>

      <div class="transaction-center d-none d-md-flex">
        <div class="account-pill" v-tooltip="{ content: transaction.account_name, distance: 15 }">
          <img :src="transaction.account_icon" width="18" alt="" class="rounded-circle">
          <span>{{ transaction.account_name }}</span>
        </div>
      </div>

      <div class="transaction-right">
        <strong class="transaction-amount">{{ formatMoney(transaction.amount) }}</strong>
        <button
          type="button"
          class="pay-action-btn d-md-none"
          @click="payTrasaction(transaction)"
          v-tooltip="{ content: payTooltip, distance: 15 }"
        >
          <i class="fa-thumbs-up" :class="payIconClass"></i>
        </button>
      </div>
    </div>

    <div class="transaction-actions d-none d-md-flex">
      <button
        type="button"
        class="pay-action-btn"
        @click="payTrasaction(transaction)"
        v-tooltip="{ content: payTooltip, distance: 15 }"
      >
        <i class="fa-thumbs-up" :class="payIconClass"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.transaction-modern-card {
  --tone-rgb: 31, 71, 118;
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(31, 71, 118, 0.08);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 6px 14px rgba(13, 32, 57, 0.04);
  overflow: hidden;
  isolation: isolate;
}

.card-income { --tone-rgb: 38, 184, 118; }
.card-expense { --tone-rgb: 239, 77, 93; }
.card-transfer { --tone-rgb: 67, 97, 238; }

.transaction-modern-card.darkmode {
  background: rgba(42, 42, 42, 0.98);
  border-color: rgba(255,255,255,0.05);
}

.transaction-modern-card.overdue {
  border-color: rgba(209, 133, 54, 0.38);
}

.transaction-click-area {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 14px;
  min-width: 0;
  cursor: pointer;
}

.transaction-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.transaction-category-icon {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 6px 12px rgba(15, 28, 48, 0.06);
}

.transaction-main-content {
  min-width: 0;
}

.transaction-top-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.transaction-title-wrap {
  min-width: 0;
}

.transaction-title {
  font-size: 16px;
  line-height: 1.25;
  font-weight: 700;
  color: #334a6e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.transaction-status-badge {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-paid {
  background: rgba(45, 206, 137, 0.14);
  color: #1f9f68;
}

.status-pending {
  background: rgba(67, 97, 238, 0.10);
  color: #3258c6;
}

.status-overdue {
  background: rgba(209, 133, 54, 0.15);
  color: #b56a1f;
}

.transaction-meta-line {
  display: flex;
  gap: 6px;
  margin-top: 5px;
  flex-wrap: wrap;
}

.category-chip,
.account-chip,
.account-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1;
}

.account-chip,
.account-pill {
  background: rgba(var(--tone-rgb), 0.08);
  color: #627591;
}

.transaction-center {
  justify-content: center;
}

.transaction-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 96px;
  text-align: right;
}

.transaction-date {
  font-size: 14px;
  color: #7a8aa2;
  margin-bottom: 3px;
}

.transaction-amount {
  font-size: 18px;
  line-height: 1.1;
  font-weight: 700;
}

.is-income .transaction-amount { color: #1ebf74; }
.is-expense .transaction-amount { color: #ef4d5d; }
.is-transfer .transaction-amount { color: #3258c6; }

.transaction-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.pay-action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(var(--tone-rgb), 0.08);
}

.pay-action-btn i {
  font-size: 13px;
}

.darkmode .transaction-title {
  color: #eef4ff;
}

.darkmode .transaction-date,
.darkmode .account-pill,
.darkmode .account-chip {
  color: rgba(255,255,255,0.72);
}

.darkmode .account-pill,
.darkmode .account-chip,
.darkmode .pay-action-btn {
  background: rgba(255,255,255,0.06);
}

@media (max-width: 991.98px) {
  .transaction-click-area {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .transaction-center {
    display: none !important;
  }
}

@media (max-width: 767.98px) {
  .transaction-modern-card {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 12px;
  }

  .transaction-click-area {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .transaction-category-icon {
    width: 44px;
    height: 44px;
    min-width: 44px;
    font-size: 18px;
  }

  .transaction-top-line {
    align-items: flex-start;
    justify-content: start;
  }

  .transaction-title {
    font-size: 15px;
    line-height: 1.25;
  }

  .transaction-right {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-width: auto;
    text-align: left;
  }

  .transaction-date {
    margin-bottom: 0;
    font-size: 12px;
  }

  .transaction-amount {
    font-size: 17px;
  }

  .transaction-status-badge {
    font-size: 10px;
    padding: 4px 8px;
  }

  .pay-action-btn {
    width: 34px;
    height: 34px;
  }
}
</style>
